import { useCallback, useEffect, useRef, useState } from 'react';
import { guardarAlta, leerAlta, olvidarAlta } from '../lib/alta-pendiente';
import { crearNegocioRpc } from '../lib/api-negocio';
import { useApp } from '../lib/contexto';
import { Esqueleto, Marca, Tarjeta, TextoAyuda } from '../ui/base';
import { FormularioNegocio, type DatosNegocioNuevo } from '../ui/FormularioNegocio';

/**
 * Crear el negocio: el único sitio que llama a `crear_negocio`; el enrutador trae aquí a quien tiene
 * sesión y ningún negocio propio. Con el alta en el contexto crea sin preguntar; en una carga nueva
 * los datos vuelven de `localStorage` y la autorización de habeas data se marca otra vez.
 */
export function CrearNegocio() {
  const { altaVerificada, dejarAltaVerificada, sesion, recargarMembresias, elegirNegocio, salir } = useApp();
  // Se leen una sola vez: crear el negocio suelta el alta, y la pantalla no debe cambiar por eso.
  const [verificada] = useState(() => altaVerificada);
  const [guardado] = useState(() => leerAlta());
  const [ocupado, setOcupado] = useState(verificada !== null);
  const [error, setError] = useState('');

  const correo = sesion?.user.email ?? verificada?.correo ?? guardado?.correo ?? '';

  const crear = useCallback(
    async (datos: DatosNegocioNuevo) => {
      setError('');
      setOcupado(true);
      try {
        const id = await crearNegocioRpc(datos);
        dejarAltaVerificada(null);
        olvidarAlta();
        await recargarMembresias();
        // Con el negocio elegido, el enrutador ya sale de esta pantalla.
        elegirNegocio(id);
      } catch (e) {
        // La cuenta ya existe: los datos se guardan para que el formulario los devuelva puestos.
        dejarAltaVerificada(null);
        guardarAlta(correo, datos);
        setError(e instanceof Error ? e.message : 'No pudimos crear tu negocio. Revisa los datos e inténtalo de nuevo.');
        setOcupado(false);
      }
    },
    [correo, dejarAltaVerificada, elegirNegocio, recargarMembresias]
  );

  // El alta del registro se crea sola: los datos y la autorización se acaban de dar.
  const intentado = useRef(false);
  useEffect(() => {
    if (!verificada || intentado.current) return;
    intentado.current = true;
    void crear(verificada.datos);
  }, [verificada, crear]);

  if (ocupado && verificada && !error) {
    return (
      <main className="flex min-h-full justify-center px-4 py-10">
        <div className="w-full max-w-3xl">
          <Tarjeta>
            <h1 className="mb-2 text-titulo font-extrabold">Creando tu negocio…</h1>
            <TextoAyuda>Un momento: estamos dejando todo listo para cobrar.</TextoAyuda>
            <Esqueleto lineas={4} />
          </Tarjeta>
        </div>
      </main>
    );
  }

  const hayDatosPrevios = verificada !== null || guardado !== null;

  return (
    <main className="flex min-h-full justify-center px-4 py-10">
      <div className="w-full max-w-3xl">
        <div className="mb-4 text-center">
          <Marca className="text-marca-grande" variante="completo" />
          <div className="firma-degradado mx-auto mt-2 h-1 w-24 rounded-full" aria-hidden="true" />
        </div>

        <Tarjeta>
          <h1 className="mb-1 text-titulo font-extrabold">
            {hayDatosPrevios ? 'Falta un paso para terminar' : 'Crea tu negocio'}
          </h1>
          <TextoAyuda>
            {hayDatosPrevios
              ? 'Tu cuenta ya existe. Revisa los datos, confirma la autorización y quedas listo para cobrar.'
              : 'Estos son los datos de tu negocio y los tuyos como responsable.'}
          </TextoAyuda>

          <FormularioNegocio
            onListo={crear}
            ocupado={ocupado}
            errorServidor={error}
            inicial={verificada?.datos ?? guardado?.datos}
          />

          <TextoAyuda className="mt-3 mb-0">
            ¿Te invitaron a un negocio? Abre el enlace de invitación que te compartieron.
          </TextoAyuda>
          <p className="mt-2">
            <button onClick={() => void salir()} className="text-info text-contenido underline">
              Salir
            </button>
          </p>
        </Tarjeta>
      </div>
    </main>
  );
}
